/**
 * Is this tab running an out-of-date bundle?
 *
 * CRA content-hashes its bundles, so a deploy changes their filenames and an
 * open tab never asks for the new ones. It keeps running what it loaded, with
 * nothing to indicate it is stale. On 2026-08-29 that cost a duplicate OAuth
 * channel: a tab running a pre-fix bundle called `startConnection` without a
 * `channel_id`, so the callback inserted a new channel instead of reconnecting
 * the existing one. A tab has to be able to notice, and say so.
 *
 * The comparison is between two values written by ONE run of
 * scripts/write-build-stamp.js:
 *
 *   BUILD_ID          compiled into this bundle; what this tab is running
 *   /build.json       served from the deploy directory; what is deployed now
 *
 * PROMPT, NEVER FORCE. This hook reports; the banner asks. An automatic reload
 * would fire mid-form and mid-OAuth-return, and losing an admin's half-finished
 * work to fix a staleness problem is a worse outcome than the staleness.
 *
 * EVERY FAILURE IS "NOT STALE". A network blip, an offline laptop, a 502 from
 * the edge, or the SPA fallback returning index.html for a missing /build.json
 * all resolve to "no, carry on" — never to a banner. The cost of a false
 * negative is a tab that stays stale until the next check; the cost of a false
 * positive is nagging someone to reload a bundle that is already current, which
 * teaches them to ignore the banner that matters.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BUILD_ID } from '../buildId';

/**
 * The floor between checks.
 *
 * Route changes are frequent — a few clicks through the workflow designer is
 * several — and each check is a request. A minute is far finer than any real
 * deploy cadence while keeping the traffic to roughly nothing.
 */
export const STALE_CHECK_MIN_INTERVAL_MS = 60_000;

/**
 * The deployed build id, or null when it cannot be determined.
 *
 * null is the "do not conclude anything" answer and covers every failure the
 * caller must not act on. It is deliberately NOT distinguished from a
 * successfully-fetched matching id: both mean "no banner", and giving the caller
 * two ways to say that invites one of them to be handled wrongly.
 *
 * `?t=` plus `cache: 'no-store'` because this file MUST NOT come from a cache —
 * a cached copy is the previous deploy's answer, which is precisely the state
 * being detected. Caddy also serves it `Cache-Control: no-cache`; the query
 * buster is belt-and-braces against an intermediary that ignores that.
 */
export async function fetchLatestBuildId() {
  const res = await fetch(`/build.json?t=${Date.now()}`, { cache: 'no-store' });
  const body = await res.json();
  const id = body && body.build_id;
  return typeof id === 'string' && id ? id : null;
}

/**
 * True once this tab is known to be running an older bundle than the deploy.
 *
 * ONE-WAY. Once true it stays true until the page reloads: a deploy does not get
 * un-deployed, and a subsequent failed check must not clear a banner that is
 * correct. It also stops checking at that point — the answer will not change.
 */
export function useBuildStaleness() {
  const [stale, setStale] = useState(false);
  const location = useLocation();

  /**
   * Refs, not state, for both: neither should cause a render, and the throttle
   * in particular must be readable synchronously by a check that is deciding
   * whether to run at all.
   */
  const lastCheckedAt = useRef(0);
  const announced = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const check = useCallback(async () => {
    if (stale) return;

    // Starts at 0, so the first call always passes: mount is not a special case
    // needing a force flag, it is simply the first check.
    const now = Date.now();
    if (now - lastCheckedAt.current < STALE_CHECK_MIN_INTERVAL_MS) return;
    // Stamped BEFORE the await, not after. Two triggers landing together — a
    // route change and a tab focus — would otherwise both pass the gate and
    // both fetch.
    lastCheckedAt.current = now;

    let latest;
    try {
      latest = await fetchLatestBuildId();
    } catch {
      // Network, CORS, a non-JSON body (the SPA fallback serving index.html for
      // a build that predates the stamp). Never a banner.
      return;
    }

    if (!latest || latest === BUILD_ID) return;
    if (!mounted.current) return;

    if (!announced.current) {
      announced.current = true;
      // Once, for support: this is what turns "the page was behaving oddly" into
      // a specific pair of build ids in a console someone can read back.
      console.info('[build] stale bundle detected', { running: BUILD_ID, latest });
    }
    setStale(true);
  }, [stale]);

  // Mount, and every route change. The throttle inside `check` is what keeps
  // this from being one request per click.
  useEffect(() => {
    check();
  }, [location.pathname, check]);

  // Returning to the tab is the highest-value moment to check: a tab left open
  // for a day is exactly the one running an old bundle, and the user is about to
  // interact with it.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [check]);

  return stale;
}
